import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Target, Eye, Award, Users, Globe, TrendingUp, Building, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { mockData } from '../mockData';

const AboutPage = () => {
  const { t } = useTranslation();
  const [heroSlide, setHeroSlide] = useState(0);
  const [ctaSlide, setCtaSlide] = useState(0);
  
  // Hero carousel images
  const heroSlides = [
    "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&q=90", // Team collaboration
    "https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&q=90", // Business strategy
    mockData.drcImages.city2, // DRC city
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=90"  // Professional meeting
  ];
  
  // Premium DRC business images for CTA carousel
  const ctaSlides = [
    "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=90", // Modern buildings
    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&q=90", // Business meeting
    mockData.drcImages.city1, // DRC city
    "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=90"  // Office workspace
  ];
  
  // Auto-advance hero carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);
  
  // Auto-advance CTA carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCtaSlide((prev) => (prev + 1) % ctaSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [ctaSlides.length]);
  
  const values = [
    {
      icon: Target,
      title: t('about.integrity'),
      description: t('about.integrityDesc')
    },
    {
      icon: Eye,
      title: t('about.excellence'),
      description: t('about.excellenceDesc')
    },
    {
      icon: Award,
      title: t('about.partnership'),
      description: t('about.partnershipDesc')
    },
    {
      icon: Users,
      title: t('about.innovation'),
      description: t('about.innovationDesc')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Premium Hero Section with Carousel */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[500px]">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={heroSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2.5, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[heroSlide]})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/70 to-blue-800/60"></div>
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Content */}
        <div className="max-w-7xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-6 py-2 text-sm mb-6">
              {t('about.title')}
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">{t('about.subtitle')}</h1>
            <p className="text-2xl text-blue-100 max-w-3xl mx-auto font-light">
              {t('home.tagline')}
            </p>
            
            {/* Slide Indicators */}
            <div className="flex gap-2 justify-center pt-8">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setHeroSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === heroSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-500/20 to-blue-700/20 rounded-3xl blur-2xl"></div>
              <img
                src={mockData.drcImages.ceo}
                alt="CEO CHEKINAH OLIVIER"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover border-4 border-white"
              />
            </div>
            <div className="space-y-6">
              <Badge className="bg-blue-100 text-blue-900 px-4 py-2">
                {t('about.leadership')}
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                {t('about.ledBy')} <span className="text-blue-900">CHEKINAH OLIVIER</span>
              </h2>
              <p className="text-xl font-semibold text-blue-800">Founder & CEO</p>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  {t('about.ceoDesc1')}
                </p>
                <p>
                  {t('about.ceoDesc2')}
                </p>
                <p>
                  {t('about.ceoDesc3')}
                </p>
              </div>
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700 text-white px-8 py-6 text-lg shadow-lg group">
                  {t('common.contactUs')}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Company Overview with Carousel */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={heroSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 3, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroSlides[heroSlide]})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/85 via-blue-800/80 to-blue-900/85"></div>
            
            {/* Decorative Elements */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '50px 50px'}}></div>
            </div>
            
            {/* Animated Gradient Orbs */}
            <motion.div
              className="absolute top-20 left-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
              animate={{
                scale: [1.2, 1, 1.2],
                opacity: [0.5, 0.3, 0.5],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        </AnimatePresence>

        {/* Content */}
        <div className="max-w-5xl mx-auto relative">
          <motion.div 
            className="text-center space-y-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Badge */}
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-8 py-3 text-base">
              <Building className="w-5 h-5 mr-2" />
              {t('about.whoWeAre')}
            </Badge>

            {/* Main Title */}
            <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight">
              Your Strategic Partner for
              <span className="block text-yellow-400 mt-2">Business Success in the DRC</span>
            </h2>

            {/* Description Cards */}
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              {/* Card 1 */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 text-left"
              >
                <div className="bg-white/20 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Who We Are</h3>
                <p className="text-blue-100 leading-relaxed">
                  {t('about.whoWeAreDesc')}
                </p>
              </motion.div>

              {/* Card 2 */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 text-left"
              >
                <div className="bg-yellow-400/20 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-7 h-7 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Our Mission</h3>
                <p className="text-blue-100 leading-relaxed">
                  {t('about.ourMissionDesc')}
                </p>
              </motion.div>
            </div>

            {/* Stats Row */}
            <motion.div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 pt-12 border-t border-white/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">10+</div>
                <div className="text-blue-200 text-sm">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">500+</div>
                <div className="text-blue-200 text-sm">Projects Completed</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">50+</div>
                <div className="text-blue-200 text-sm">Partners Worldwide</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">98%</div>
                <div className="text-blue-200 text-sm">Client Satisfaction</div>
              </div>
            </motion.div>

            {/* Slide Indicators */}
            <div className="flex gap-2 justify-center pt-8">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setHeroSlide(index)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === heroSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-900 px-6 py-2 text-sm mb-4">
              {t('about.ourValues')}
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">{t('about.whyChooseTitle')}</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="border-2 hover:border-blue-500 hover:shadow-xl transition-all duration-300 group">
                  <CardHeader>
                    <div className="bg-blue-100 w-16 h-16 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                      <IconComponent className="w-8 h-8 text-blue-900 group-hover:text-white transition-colors" />
                    </div>
                    <CardTitle className="text-2xl text-blue-900">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-lg leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Experience OL-CONSULT</h2>
            <p className="text-xl text-gray-600">Watch our corporate presentation videos</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">English Presentation</h3>
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <video controls className="w-full h-auto" poster={mockData.drcImages.city1}>
                  <source src={mockData.videos.english} type="video/mp4" />
                </video>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">Présentation Française</h3>
              <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <video controls className="w-full h-auto" poster={mockData.drcImages.city2}>
                  <source src={mockData.videos.french} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OL CONSULT Group Companies */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-900 px-6 py-2 mb-4">OL CONSULT Group</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Family of Companies</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The OL CONSULT Group operates across multiple sectors, delivering excellence in real estate, project management, mining, and investment advisory services worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* OL Plazas */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="group"
            >
              <Card className="border-2 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 h-full overflow-hidden">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src="https://olplazas.com/wp-content/uploads/2025/07/ol-plazas-image.webp" 
                    alt="OL Plazas"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <img 
                      src="https://olplazas.com/wp-content/uploads/2025/07/banner-slider-logo.png" 
                      alt="OL Plazas Logo"
                      className="h-12 w-auto"
                    />
                  </div>
                </div>
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">OL Plazas</h3>
                  <p className="text-lg font-semibold text-blue-800 mb-4">Building Value in Real Estate</p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    OL Plazas specializes in high-performing commercial, residential, and mixed-use investments, delivering long-term growth and sustainable returns for partners. From redevelopment to asset management, we turn underperforming properties into profitable real estate investments with enduring value.
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <h4 className="font-bold text-gray-900">Core Services:</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">Property Management</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">Project Management</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">Development</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">Investment Advisory</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Location:</strong> Greater Toronto Area (GTA) - Mississauga, Brampton, Oakville, Toronto
                    </p>
                    <a 
                      href="https://olplazas.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-900 font-semibold hover:text-blue-700 transition-colors group"
                    >
                      Visit OL Plazas Website
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* OLC Project Management */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group"
            >
              <Card className="border-2 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 h-full overflow-hidden">
                <div className="relative h-64 overflow-hidden bg-gradient-to-br from-blue-900 to-blue-800">
                  <img 
                    src="https://static.wixstatic.com/media/f6a44e066e384d8b887851294d2e6994.jpg/v1/fill/w_1904,h_992,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/f6a44e066e384d8b887851294d2e6994.jpg" 
                    alt="OLC PM Singapore"
                    className="w-full h-full object-cover opacity-80 group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-3xl font-bold text-white">OLC PM</h3>
                  </div>
                </div>
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">OLC Project Management</h3>
                  <p className="text-lg font-semibold text-blue-800 mb-4">Bridging the Pacific with Expert Knowledge</p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    OLC Project Management aims to bridge real estate opportunities between Asia and Canada by showcasing real estate deals in Singapore and Southeast Asia to Canadian investors, providing expertise from Singapore for optimizing real estate projects in Canada, and offering Canadian experts for mining projects in the Asia Pacific region.
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <h4 className="font-bold text-gray-900">Focus Areas:</h4>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">Real estate opportunities in Singapore & Southeast Asia</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">Singapore expertise for Canadian real estate projects</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700">Canadian experts for mining projects in Asia Pacific</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-3 mb-4">
                      <img 
                        src="https://static.wixstatic.com/media/ddcbe1_84f86adda889437a9e580632382e1aa8~mv2.png/v1/fill/w_222,h_111,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/ddcbe1_84f86adda889437a9e580632382e1aa8~mv2.png" 
                        alt="Singapore Business Federation"
                        className="h-12 w-auto"
                      />
                      <p className="text-xs text-gray-600">
                        Proud member of the Singapore Business Federation
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Headquarters:</strong> Singapore (Asia Pacific Hub)
                    </p>
                    <a 
                      href="https://www.olcpm.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-900 font-semibold hover:text-blue-700 transition-colors group"
                    >
                      Visit OLC PM Website
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Chekinah Olivier Personal Brand */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="group"
            >
              <Card className="border-2 hover:border-blue-500 hover:shadow-2xl transition-all duration-300 h-full overflow-hidden">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src="https://chekinaholivier.com/wp-content/uploads/2016/01/chekinah-Olivier-.jpg" 
                    alt="Chekinah Olivier"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-3xl font-bold text-white">Chekinah Olivier</h3>
                    <p className="text-white/90 text-sm mt-1">Serial Businessman</p>
                  </div>
                </div>
                <CardContent className="p-8 space-y-4">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">Chekinah Olivier</h3>
                  <p className="text-lg font-semibold text-blue-800 mb-4">Serial Businessman, Motivational Speaker, Coach & Mentor</p>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    With over 20 years of experience in diverse industries, entrepreneurship, and global trade, Chekinah Olivier is a seasoned businessman and visionary leader. From a young age, he displayed an entrepreneurial spirit and a keen eye for opportunities, building a legacy of innovation and empowerment across Africa and beyond.
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <h4 className="font-bold text-gray-900">Professional Activities:</h4>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700"><strong>Leadership:</strong> Supporting innovative solutions across business sectors</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700"><strong>Communication:</strong> Bringing together key actors to foster dialogue</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700"><strong>Consulting:</strong> Expert guidance in complex business environments</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-700"><strong>Mentoring:</strong> Empowering future generations of entrepreneurs</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6 bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-gray-900">Key Initiatives:</h4>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-gray-700"><strong>Mapping Africa's Resources:</strong> Helping governments negotiate with mining multinationals</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-gray-700"><strong>Empowerment:</strong> Giving countries full knowledge of their subsoil resources</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-gray-700"><strong>Sustainability:</strong> Implementing innovative solutions for a brighter future</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600 mb-4">
                      <strong>Experience:</strong> 20+ years in entrepreneurship, mining, real estate, and global trade
                    </p>
                    <a 
                      href="https://chekinaholivier.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-900 font-semibold hover:text-blue-700 transition-colors group"
                    >
                      Learn More About Chekinah Olivier
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Premium CTA with Carousel */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Carousel */}
        <AnimatePresence mode="sync">
          <motion.div
            key={ctaSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${ctaSlides[ctaSlide]})` }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 via-blue-900/70 to-blue-800/60"></div>
            
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
            </div>
          </motion.div>
        </AnimatePresence>
        
        {/* Content */}
        <div className="relative max-w-5xl mx-auto text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Badge className="bg-white/20 backdrop-blur-sm text-white border-white/30 px-6 py-2 text-sm mb-6">
              <Globe className="w-4 h-4 mr-2" />
              Start Your Journey
            </Badge>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Ready to Start Your DRC Journey?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 font-light max-w-3xl mx-auto">
              Let's discuss how we can help you achieve success in the Democratic Republic of Congo
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-12 py-7 shadow-2xl hover:shadow-blue-500/50 transition-all duration-300 group"
              >
                Contact Us Today
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/services">
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 backdrop-blur-sm text-lg px-12 py-7 font-semibold"
              >
                Explore Services
              </Button>
            </Link>
          </motion.div>
          
          {/* Slide Indicators */}
          <div className="flex gap-2 justify-center pt-4">
            {ctaSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCtaSlide(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === ctaSlide ? 'w-8 bg-white' : 'w-4 bg-white/40'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
